/**
 * Pure, allowlist-first observability scrubber.
 *
 * The caller supplies an exact schema. Unknown keys are never read, copied, or
 * serialized. This module intentionally has no rule for arbitrary free text.
 */

export const OBSERVABILITY_SCRUBBER_VERSION = "os1" as const;

export interface ObservabilityLimits {
  maxDepth: number;
  maxObjectKeys: number;
  maxArrayItems: number;
  maxStringLength: number;
  maxOutputBytes: number;
}

export const DEFAULT_OBSERVABILITY_LIMITS: Readonly<ObservabilityLimits> = {
  maxDepth: 4,
  maxObjectKeys: 32,
  maxArrayItems: 32,
  maxStringLength: 160,
  maxOutputBytes: 4_096,
};

type EnumValue = string | number | boolean;

type BaseRule = { nullable?: boolean; outputKey?: string };

export type ObservabilityRule = BaseRule & (
  | { kind: "boolean" }
  | { kind: "number"; integer?: boolean; min?: number; max?: number }
  | { kind: "enum"; values: readonly EnumValue[] }
  | { kind: "digest"; version?: string }
  | { kind: "error" }
  | { kind: "object"; policy: ObservabilityPolicy }
  | { kind: "array"; item: ObservabilityRule; maxItems?: number }
);

export interface ObservabilityPolicy {
  fields: Readonly<Record<string, ObservabilityRule>>;
}

export interface SafeErrorMetadata {
  error_type: "type_error" | "range_error" | "syntax_error" | "error" | "unknown_error";
  error_code: "timeout" | "network" | "rate_limited" | "upstream_rejected" | "validation" | "unknown";
  status_code?: number;
  timeout?: boolean;
}

interface ScrubState {
  readonly limits: ObservabilityLimits;
  readonly seen: WeakSet<object>;
  remainingBytes: number;
}

const BLOCKED_OUTPUT_KEYS = new Set(["__proto__", "prototype", "constructor"]);
const DIGEST_RE = /^op\d+_[A-Za-z0-9_-]{20,128}$/;
const TIMEOUT_CODES = new Set(["ETIMEDOUT", "ESOCKETTIMEDOUT", "UND_ERR_CONNECT_TIMEOUT", "ABORT_ERR"]);
const NETWORK_CODES = new Set(["ECONNRESET", "ECONNREFUSED", "ENETUNREACH", "EAI_AGAIN", "ENOTFOUND", "UND_ERR_CONNECT"]);
const RATE_LIMIT_CODES = new Set(["RATE_LIMIT", "RATE_LIMITED", "TOO_MANY_REQUESTS"]);
const VALIDATION_CODES = new Set(["VALIDATION_ERROR", "INVALID_ARGUMENT", "BAD_REQUEST"]);

function createState(limits?: Partial<ObservabilityLimits>): ScrubState {
  const resolved = { ...DEFAULT_OBSERVABILITY_LIMITS, ...limits };
  return {
    limits: resolved,
    seen: new WeakSet<object>(),
    remainingBytes: resolved.maxOutputBytes,
  };
}

function consumeBudget(state: ScrubState, amount: number): boolean {
  if (!Number.isSafeInteger(amount) || amount < 0 || amount > state.remainingBytes) return false;
  state.remainingBytes -= amount;
  return true;
}

function safeDescriptor(value: object, key: string): PropertyDescriptor | undefined {
  try {
    return Object.getOwnPropertyDescriptor(value, key);
  } catch {
    return undefined;
  }
}

function safeOwnStringKeys(value: object): string[] | undefined {
  try {
    return Reflect.ownKeys(value).filter((key): key is string => typeof key === "string").sort();
  } catch {
    return undefined;
  }
}

function safeInstanceOf(value: unknown, constructor: Function): boolean {
  try {
    return value instanceof (constructor as typeof Error);
  } catch {
    return false;
  }
}

function readOwnDataValue(value: object, key: string): unknown {
  const descriptor = safeDescriptor(value, key);
  if (!descriptor || !("value" in descriptor)) return undefined;
  return descriptor.value;
}

function classifyErrorType(value: unknown): SafeErrorMetadata["error_type"] {
  if (safeInstanceOf(value, TypeError)) return "type_error";
  if (safeInstanceOf(value, RangeError)) return "range_error";
  if (safeInstanceOf(value, SyntaxError)) return "syntax_error";
  if (safeInstanceOf(value, Error)) return "error";
  return "unknown_error";
}

/**
 * Reduce an error-like value without reading message, stack, cause, response,
 * body, headers, or accessors.
 */
export function classifyObservabilityError(value: unknown): SafeErrorMetadata {
  const metadata: SafeErrorMetadata = {
    error_type: classifyErrorType(value),
    error_code: "unknown",
  };

  if ((typeof value !== "object" && typeof value !== "function") || value === null) {
    return metadata;
  }

  let timedOut = false;
  const codeValue = readOwnDataValue(value, "code");
  if (typeof codeValue === "string") {
    const code = codeValue.toUpperCase();
    if (TIMEOUT_CODES.has(code)) {
      metadata.error_code = "timeout";
      timedOut = true;
    } else if (NETWORK_CODES.has(code)) metadata.error_code = "network";
    else if (RATE_LIMIT_CODES.has(code)) metadata.error_code = "rate_limited";
    else if (VALIDATION_CODES.has(code)) metadata.error_code = "validation";
  }

  const statusValue = readOwnDataValue(value, "status") ?? readOwnDataValue(value, "statusCode");
  if (typeof statusValue === "number" && Number.isInteger(statusValue) && statusValue >= 100 && statusValue <= 599) {
    metadata.status_code = statusValue;
    if (statusValue === 429) metadata.error_code = "rate_limited";
    else if (statusValue >= 400) metadata.error_code = "upstream_rejected";
  }

  if (timedOut) metadata.timeout = true;
  return metadata;
}

function scrubError(value: unknown, state: ScrubState): SafeErrorMetadata | undefined {
  const metadata = classifyObservabilityError(value);
  const estimatedBytes = 64 + (metadata.status_code === undefined ? 0 : 8);
  return consumeBudget(state, estimatedBytes) ? metadata : undefined;
}

function scrubRule(
  value: unknown,
  rule: ObservabilityRule,
  state: ScrubState,
  depth: number,
): unknown {
  if (value === null) return rule.nullable ? null : undefined;

  switch (rule.kind) {
    case "boolean":
      return typeof value === "boolean" && consumeBudget(state, 5) ? value : undefined;
    case "number": {
      if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
      if (rule.integer && !Number.isInteger(value)) return undefined;
      if (rule.min !== undefined && value < rule.min) return undefined;
      if (rule.max !== undefined && value > rule.max) return undefined;
      return consumeBudget(state, 24) ? value : undefined;
    }
    case "enum": {
      if (!rule.values.some((candidate) => Object.is(candidate, value))) return undefined;
      const bytes = typeof value === "string" ? value.length + 2 : 16;
      if (typeof value === "string" && value.length > state.limits.maxStringLength) return undefined;
      return consumeBudget(state, bytes) ? value : undefined;
    }
    case "digest": {
      if (typeof value !== "string" || value.length > state.limits.maxStringLength || !DIGEST_RE.test(value)) {
        return undefined;
      }
      if (rule.version && !value.startsWith(`${rule.version}_`)) return undefined;
      return consumeBudget(state, value.length + 2) ? value : undefined;
    }
    case "error":
      return scrubError(value, state);
    case "object":
      return scrubObject(value, rule.policy, state, depth + 1);
    case "array": {
      if (!Array.isArray(value) || depth >= state.limits.maxDepth || state.seen.has(value)) return undefined;
      state.seen.add(value);
      const lengthDescriptor = safeDescriptor(value, "length");
      const rawLength = lengthDescriptor && "value" in lengthDescriptor ? lengthDescriptor.value : 0;
      if (typeof rawLength !== "number" || !Number.isSafeInteger(rawLength) || rawLength < 0) return undefined;
      const limit = Math.min(rule.maxItems ?? state.limits.maxArrayItems, state.limits.maxArrayItems);
      const output: unknown[] = [];
      const count = Math.min(rawLength, limit);
      let omitted = Math.max(0, rawLength - count);
      for (let index = 0; index < count; index += 1) {
        const descriptor = safeDescriptor(value, String(index));
        if (!descriptor || !("value" in descriptor)) {
          omitted += 1;
          continue;
        }
        const safe = scrubRule(descriptor.value, rule.item, state, depth + 1);
        if (safe === undefined) omitted += 1;
        else output.push(safe);
      }
      if (omitted > 0 && consumeBudget(state, 24)) output.push({ observability_omitted: omitted });
      return output;
    }
  }
}

function scrubObject(
  input: unknown,
  policy: ObservabilityPolicy,
  state: ScrubState,
  depth: number,
): Record<string, unknown> | undefined {
  if ((typeof input !== "object" && typeof input !== "function") || input === null) return undefined;
  if (depth > state.limits.maxDepth || state.seen.has(input)) return undefined;
  state.seen.add(input);

  const ownKeys = safeOwnStringKeys(input);
  if (!ownKeys) return undefined;

  const output: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  const boundedKeys = ownKeys.slice(0, state.limits.maxObjectKeys);
  let omitted = Math.max(0, ownKeys.length - boundedKeys.length);

  for (const key of boundedKeys) {
    const rule = policy.fields[key];
    if (!rule) {
      omitted += 1;
      continue;
    }
    const outputKey = rule.outputKey ?? key;
    if (BLOCKED_OUTPUT_KEYS.has(outputKey) || Object.hasOwn(output, outputKey)) {
      omitted += 1;
      continue;
    }
    const descriptor = safeDescriptor(input, key);
    if (!descriptor || !("value" in descriptor)) {
      omitted += 1;
      continue;
    }
    if (!consumeBudget(state, outputKey.length + 4)) break;
    const safeValue = scrubRule(descriptor.value, rule, state, depth);
    if (safeValue === undefined) omitted += 1;
    else output[outputKey] = safeValue;
  }

  if (omitted > 0 && !Object.hasOwn(output, "observability_omitted") && consumeBudget(state, 36)) {
    output.observability_omitted = Math.min(omitted, Number.MAX_SAFE_INTEGER);
  }
  return output;
}

/** Scrub an object according to an exact field policy. Unknown data is dropped. */
export function scrubObservability(
  input: unknown,
  policy: ObservabilityPolicy,
  limits?: Partial<ObservabilityLimits>,
): Record<string, unknown> {
  return scrubObject(input, policy, createState(limits), 0) ?? Object.create(null);
}

/** Serialize only an already-scrubbed value, with a fail-closed fallback. */
export function serializeScrubbedObservability(input: Record<string, unknown>): string {
  try {
    return JSON.stringify(input);
  } catch {
    return '{"message":"observability_serialization_failed"}';
  }
}
